import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, KV, Modal, PageHead, SearchBox, Select,
  StatCard, Tabs, dateTimeStr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { companyApi, shipmentApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'requested', label: 'Awaiting partner' },
  { value: 'assigned,picked_up', label: 'Picked up' },
  { value: 'in_transit,out_for_delivery', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
];

const Shipments = () => {
  const toast = useToast();
  const list = useList(shipmentApi.list, { status: '', search: '' });

  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [detail, setDetail] = useState(null);
  const [assign, setAssign] = useState(null);
  const [assignForm, setAssignForm] = useState({ logisticsPartnerId: '', trackingNumber: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    shipmentApi.stats().then(res => setStats(res.data)).catch(() => {});
    companyApi
      .list({ type: 'logistics', verificationStatus: 'approved', limit: 100 })
      .then(res => setPartners(res.data))
      .catch(() => {});
  }, []);

  const doAssign = async () => {
    if (!assignForm.logisticsPartnerId) return toast.error('Pick a logistics partner');

    setBusy(true);
    try {
      await shipmentApi.assign(assign._id, assignForm);
      toast.success('Logistics partner assigned');
      setAssign(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const columns = [
    {
      key: 'shipmentNumber',
      label: 'Shipment',
      render: row => (
        <>
          <div className="cell-strong mono">{row.shipmentNumber}</div>
          {row.trackingNumber && <div className="cell-muted">AWB {row.trackingNumber}</div>}
        </>
      ),
    },
    { key: 'order', label: 'Order', render: row => <span className="mono text-sm">{row.order?.orderNumber || '—'}</span> },
    {
      key: 'route',
      label: 'Route',
      render: row => (
        <>
          <div className="text-sm">{row.pickup?.city || '—'} → {row.delivery?.city || '—'}</div>
          <div className="cell-muted">{row.delivery?.pincode}</div>
        </>
      ),
    },
    {
      key: 'partner',
      label: 'Logistics partner',
      render: row => (row.logisticsPartner
        ? <IdCell image={row.logisticsPartner.logo} name={row.logisticsPartner.name} sub={row.logisticsPartner.code} size="sm" />
        : <span className="text-muted">Unassigned</span>),
    },
    { key: 'packageCount', label: 'Pkgs', align: 'center', render: row => num(row.packageCount) },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          {!row.logisticsPartner && (
            <Button
              size="sm"
              onClick={e => { e.stopPropagation(); setAssign(row); setAssignForm({ logisticsPartnerId: '', trackingNumber: '' }); }}
            >
              Assign
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead title="Shipments" subtitle="Pickups, transit tracking and proof of delivery." />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Total shipments" value={num(stats.total)} icon="🚚" foot={`${stats.onTimeRate}% delivered`} />
          <StatCard label="Awaiting pickup" value={num(stats.byStatus.requested + stats.byStatus.assigned)} icon="⏳" />
          <StatCard label="In transit" value={num(stats.byStatus.in_transit + stats.byStatus.picked_up)} icon="📦" />
          <StatCard label="Delivered" value={num(stats.delivered)} icon="✓" />
          <StatCard label="Failed attempts" value={num(stats.byStatus.failed)} icon="⚠" />
        </div>
      )}

      <Tabs tabs={TABS} value={list.filters.status} onChange={v => list.setFilter('status', v)} />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search shipment or AWB number…" />
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} shipments` : ''}</span>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={async row => {
          try {
            setDetail((await shipmentApi.get(row._id)).data);
          } catch (err) {
            toast.error(err.message);
          }
        }}
      />

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.shipmentNumber}
        subtitle={detail ? `Order ${detail.order?.orderNumber}` : ''}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <>
            <div className="row mb-2"><Badge status={detail.status} /></div>

            <div className="grid-2">
              <div>
                <div className="card-title mb-2">Pickup</div>
                <div className="text-sm">
                  <div className="strong">{detail.pickup?.contactName || detail.seller?.name}</div>
                  <div className="text-muted">{[detail.pickup?.address, detail.pickup?.city, detail.pickup?.state, detail.pickup?.pincode].filter(Boolean).join(', ')}</div>
                  {detail.pickup?.contactMobile && <div className="text-muted">📞 {detail.pickup.contactMobile}</div>}
                  {detail.pickup?.pickedUpAt && <div className="mt-1">Picked up {dateTimeStr(detail.pickup.pickedUpAt)}</div>}
                </div>
              </div>
              <div>
                <div className="card-title mb-2">Delivery</div>
                <div className="text-sm">
                  <div className="strong">{detail.delivery?.contactName || detail.buyer?.name}</div>
                  <div className="text-muted">{[detail.delivery?.address, detail.delivery?.city, detail.delivery?.state, detail.delivery?.pincode].filter(Boolean).join(', ')}</div>
                  {detail.delivery?.contactMobile && <div className="text-muted">📞 {detail.delivery.contactMobile}</div>}
                  {detail.delivery?.deliveredAt && <div className="mt-1">Delivered {dateTimeStr(detail.delivery.deliveredAt)}</div>}
                </div>
              </div>
            </div>

            <div className="divider" />
            <KV
              items={[
                { label: 'Logistics partner', value: detail.logisticsPartner?.name || 'Unassigned' },
                { label: 'Tracking / AWB', value: detail.trackingNumber || '—' },
                { label: 'Packages', value: `${num(detail.packageCount)} · ${num(detail.totalWeightKg)} kg` },
                detail.vehicle?.number && { label: 'Vehicle', value: `${detail.vehicle.number} (${detail.vehicle.type || 'truck'})` },
                detail.vehicle?.driverName && { label: 'Driver', value: `${detail.vehicle.driverName} · ${detail.vehicle.driverMobile}` },
                detail.ewayBillNumber && { label: 'E-way bill', value: detail.ewayBillNumber },
                { label: 'Insured', value: detail.isInsured ? 'Yes' : 'No' },
              ]}
            />

            {detail.pod?.capturedAt && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Proof of delivery</div>
                <KV
                  items={[
                    { label: 'Received by', value: `${detail.pod.receiverName} · ${detail.pod.receiverMobile}` },
                    { label: 'Captured', value: dateTimeStr(detail.pod.capturedAt) },
                    detail.pod.remarks && { label: 'Remarks', value: detail.pod.remarks },
                  ]}
                />
                <div className="row wrap mt-2">
                  {(detail.pod.photoUrls || []).map(url => (
                    <img key={url} src={url} alt="POD" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--line)' }} />
                  ))}
                  {detail.pod.signatureUrl && (
                    <img src={detail.pod.signatureUrl} alt="Signature" style={{ height: 90, borderRadius: 10, border: '1px solid var(--line)', background: '#fff' }} />
                  )}
                </div>
              </>
            )}

            <div className="divider" />
            <div className="card-title mb-2">Tracking events</div>
            <div className="timeline">
              {[...(detail.trackingEvents || [])].reverse().map((event, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className={`tl-item ${index === 0 ? 'current' : 'done'}`}>
                  <div className="tl-title">{titleCase(event.status)}</div>
                  <div className="tl-time">{dateTimeStr(event.at)}{event.location ? ` · ${event.location}` : ''}</div>
                  {event.description && <div className="text-sm text-muted">{event.description}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!assign}
        title="Assign a logistics partner"
        subtitle={assign?.shipmentNumber}
        onClose={() => setAssign(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAssign(null)}>Cancel</Button>
            <Button loading={busy} onClick={doAssign}>Assign & notify</Button>
          </>
        }
      >
        <Select
          label="Logistics partner"
          value={assignForm.logisticsPartnerId}
          onChange={e => setAssignForm({ ...assignForm, logisticsPartnerId: e.target.value })}
          placeholder="Select a verified partner…"
          options={partners.map(p => ({ value: p._id, label: `${p.name} (${p.code})` }))}
        />
        <div className="field">
          <label>Tracking / AWB number (optional)</label>
          <input
            className="input"
            value={assignForm.trackingNumber}
            onChange={e => setAssignForm({ ...assignForm, trackingNumber: e.target.value })}
            placeholder="AWB123456789"
          />
        </div>
      </Modal>
    </>
  );
};

export default Shipments;
